import type { ComponentType } from 'react'
import {
  Calendar,
  Users,
  Activity,
  Scissors,
  Package,
  CreditCard,
  ClipboardList,
  BarChart3,
  Building2,
  Shield,
  ShieldCheck,
  ScrollText,
  BellRing,
  FileText,
  UserPlus,
  Stethoscope,
  Receipt,
  Settings,
  Bell,
  UserCircle,
  FileArchive,
  HeartPulse,
  Mail,
  Phone,
  Printer,
  Download,
  Upload,
  FlaskConical,
  FileSignature,
  Monitor,
  Megaphone,
  Star,
  ClipboardCheck,
  Truck,
  Landmark,
  Armchair,
} from 'lucide-react'

export type CommandActionCategory = 'Acciones' | 'Navegacion' | 'Pacientes' | 'Laboratorio' | 'Finanzas' | 'Marketing' | 'Reputacion'

export type CommandActionDef = {
  id: string
  label: string
  description?: string
  category: CommandActionCategory
  icon: ComponentType<{ className?: string }>
  path: string
  keywords?: string[]
  shortcut?: string[]
  permission?: string | string[]
}

export const ALL_COMMAND_ACTIONS: CommandActionDef[] = [
  // Acciones
  {
    id: 'action.new-appointment',
    label: 'Nueva cita',
    description: 'Crear una cita rapida',
    category: 'Acciones',
    icon: Calendar,
    path: '/appointments?new=1',
    keywords: ['agenda', 'cita', 'crear', 'reservar', 'nueva'],
    shortcut: ['N'],
    permission: ['appointments:manage', 'appointments:read'],
  },
  {
    id: 'action.new-patient',
    label: 'Nuevo paciente',
    description: 'Dar de alta un paciente',
    category: 'Acciones',
    icon: UserPlus,
    path: '/patients?new=1',
    keywords: ['paciente', 'alta', 'crear', 'nuevo'],
    permission: ['patients:manage', 'patients:read'],
  },
  {
    id: 'action.new-budget',
    label: 'Nuevo presupuesto',
    description: 'Crear presupuesto',
    category: 'Acciones',
    icon: FileText,
    path: '/budgets?new=1',
    keywords: ['presupuesto', 'budget', 'crear'],
    permission: ['budgets:manage', 'budgets:read'],
  },
  {
    id: 'action.new-invoice',
    label: 'Cobrar / Nueva factura',
    description: 'Facturar y registrar cobro',
    category: 'Acciones',
    icon: Receipt,
    path: '/billing?new=1',
    keywords: ['cobrar', 'factura', 'pago', 'invoice', 'cobro'],
    permission: ['billing:manage', 'billing:read'],
  },
  {
    id: 'action.new-treatment',
    label: 'Nuevo tratamiento',
    description: 'Crear plan de tratamiento',
    category: 'Acciones',
    icon: Stethoscope,
    path: '/treatments?new=1',
    keywords: ['tratamiento', 'plan', 'crear'],
    permission: ['treatments:manage', 'treatments:read'],
  },
  {
    id: 'action.new-reminder',
    label: 'Nuevo recordatorio',
    description: 'Crear recordatorio de paciente',
    category: 'Acciones',
    icon: BellRing,
    path: '/reminders?new=1',
    keywords: ['recordatorio', 'aviso', 'crear'],
    permission: ['reminders:manage', 'reminders:read'],
  },
  {
    id: 'action.new-evolution',
    label: 'Nueva evolucion',
    description: 'Registrar evolucion clinica',
    category: 'Acciones',
    icon: HeartPulse,
    path: '/patients',
    keywords: ['evolucion', 'historia', 'clinica', 'nota'],
    permission: ['evolutions:manage', 'evolutions:read'],
  },
  {
    id: 'action.new-document',
    label: 'Nuevo documento',
    description: 'Subir documento a paciente',
    category: 'Acciones',
    icon: FileArchive,
    path: '/patients',
    keywords: ['documento', 'archivo', 'subir', 'pdf'],
    permission: ['patients:read', 'patients:manage'],
  },
  {
    id: 'action.new-clinic',
    label: 'Nueva clinica',
    description: 'Registrar nueva clinica',
    category: 'Acciones',
    icon: Building2,
    path: '/clinics?new=1',
    keywords: ['clinica', 'sede', 'crear', 'nueva'],
    permission: ['clinics:manage', 'clinics:read'],
  },
  {
    id: 'action.new-user',
    label: 'Nuevo usuario',
    description: 'Dar de alta un usuario',
    category: 'Acciones',
    icon: UserPlus,
    path: '/users?new=1',
    keywords: ['usuario', 'empleado', 'doctor', 'crear'],
    permission: ['users:manage'],
  },
  {
    id: 'action.send-email',
    label: 'Enviar email',
    description: 'Enviar email a paciente',
    category: 'Acciones',
    icon: Mail,
    path: '/reminders',
    keywords: ['email', 'correo', 'enviar', 'mensaje'],
    permission: ['reminders:read'],
  },
  {
    id: 'action.make-call',
    label: 'Llamar paciente',
    description: 'Registrar llamada telefonica',
    category: 'Acciones',
    icon: Phone,
    path: '/patients',
    keywords: ['llamar', 'telefono', 'contacto'],
    permission: ['patients:read'],
  },
  {
    id: 'action.print',
    label: 'Imprimir',
    description: 'Imprimir documento actual',
    category: 'Acciones',
    icon: Printer,
    path: '#print',
    keywords: ['imprimir', 'print', 'pdf'],
  },
  {
    id: 'action.export',
    label: 'Exportar datos',
    description: 'Exportar informes o datos',
    category: 'Acciones',
    icon: Download,
    path: '/reports',
    keywords: ['exportar', 'download', 'descargar', 'excel', 'csv'],
    permission: ['analytics:read'],
  },
  {
    id: 'action.import',
    label: 'Importar pacientes',
    description: 'Importar lista de pacientes',
    category: 'Acciones',
    icon: Upload,
    path: '/patients?import=1',
    keywords: ['importar', 'upload', 'cargar', 'excel'],
    permission: ['patients:manage'],
  },

  // Navegacion
  {
    id: 'nav.dashboard',
    label: 'Dashboard',
    category: 'Navegacion',
    icon: BarChart3,
    path: '/',
    keywords: ['inicio', 'home'],
  },
  {
    id: 'nav.agenda',
    label: 'Agenda / Calendario',
    category: 'Navegacion',
    icon: Calendar,
    path: '/appointments',
    keywords: ['citas', 'calendario'],
    permission: 'appointments:read',
  },
  {
    id: 'nav.waitlist',
    label: 'Lista de espera',
    category: 'Navegacion',
    icon: ClipboardList,
    path: '/waitlist',
    permission: 'waitlist:read',
  },
  {
    id: 'nav.patients',
    label: 'Pacientes',
    category: 'Navegacion',
    icon: Users,
    path: '/patients',
    permission: 'patients:read',
  },
  {
    id: 'nav.odontogram',
    label: 'Odontograma',
    category: 'Navegacion',
    icon: Activity,
    path: '/odontogram',
    permission: 'odontogram:read',
  },
  {
    id: 'nav.treatments',
    label: 'Tratamientos',
    category: 'Navegacion',
    icon: Scissors,
    path: '/treatments',
    permission: 'treatments:read',
  },
  {
    id: 'nav.billing',
    label: 'Facturacion',
    category: 'Navegacion',
    icon: CreditCard,
    path: '/billing',
    permission: 'billing:read',
  },
  {
    id: 'nav.budgets',
    label: 'Presupuestos',
    category: 'Navegacion',
    icon: FileText,
    path: '/budgets',
    permission: 'budgets:read',
  },
  {
    id: 'nav.insurance',
    label: 'Seguros',
    category: 'Navegacion',
    icon: Shield,
    path: '/insurance',
    permission: 'insurance:read',
  },
  {
    id: 'nav.inventory',
    label: 'Inventario / Stock',
    category: 'Navegacion',
    icon: Package,
    path: '/inventory',
    permission: 'inventory:read',
  },
  {
    id: 'nav.reminders',
    label: 'Recordatorios',
    category: 'Navegacion',
    icon: BellRing,
    path: '/reminders',
    permission: 'reminders:read',
  },
  {
    id: 'nav.reports',
    label: 'Informes / Analitica',
    category: 'Navegacion',
    icon: BarChart3,
    path: '/reports',
    permission: 'analytics:read',
  },
  {
    id: 'nav.clinics',
    label: 'Clinicas',
    category: 'Navegacion',
    icon: Building2,
    path: '/clinics',
    permission: 'clinics:read',
  },
  {
    id: 'nav.users',
    label: 'Usuarios',
    category: 'Navegacion',
    icon: Users,
    path: '/users',
    permission: 'users:read',
  },
  {
    id: 'nav.permissions',
    label: 'Permisos',
    category: 'Navegacion',
    icon: ShieldCheck,
    path: '/permissions',
    permission: 'users:manage',
  },
  {
    id: 'nav.audit',
    label: 'Auditoria',
    category: 'Navegacion',
    icon: ScrollText,
    path: '/audit',
    permission: 'users:manage',
  },
  {
    id: 'nav.profile',
    label: 'Mi perfil',
    category: 'Navegacion',
    icon: UserCircle,
    path: '/profile',
  },
  {
    id: 'nav.notifications',
    label: 'Notificaciones',
    category: 'Navegacion',
    icon: Bell,
    path: '/notifications',
  },
  {
    id: 'nav.settings',
    label: 'Configuracion',
    category: 'Navegacion',
    icon: Settings,
    path: '/settings',
  },

  // Fase 1: Consentimientos + Laboratorio
  {
    id: 'nav.consents',
    label: 'Consentimientos',
    category: 'Navegacion',
    icon: FileSignature,
    path: '/consents',
    permission: 'consents:read',
  },
  {
    id: 'nav.lab',
    label: 'Laboratorio Dental',
    category: 'Navegacion',
    icon: FlaskConical,
    path: '/lab',
    permission: 'lab:read',
  },

  // Fase 2: Contabilidad + Recursos
  {
    id: 'nav.accounting',
    label: 'Contabilidad',
    category: 'Navegacion',
    icon: Landmark,
    path: '/accounting',
    permission: 'accounting:read',
  },
  {
    id: 'nav.resources',
    label: 'Recursos',
    category: 'Navegacion',
    icon: Armchair,
    path: '/resources',
    permission: 'resources:read',
  },

  // Fase 3: Sala de espera + Marketing
  {
    id: 'nav.reception',
    label: 'Recepcion / Check-in',
    category: 'Navegacion',
    icon: Monitor,
    path: '/reception/checkin',
    permission: 'reception:manage',
  },
  {
    id: 'nav.marketing',
    label: 'Marketing',
    category: 'Navegacion',
    icon: Megaphone,
    path: '/marketing',
    permission: 'marketing:read',
  },

  // Fase 4: Reputacion + KPIs + Proveedores
  {
    id: 'nav.reputation',
    label: 'Reputacion Online',
    category: 'Navegacion',
    icon: Star,
    path: '/reputation',
    permission: 'reputation:read',
  },
  {
    id: 'nav.kpis',
    label: 'KPIs Clinicos',
    category: 'Navegacion',
    icon: ClipboardCheck,
    path: '/clinical-kpi',
    permission: 'clinical_kpis:read',
  },
  {
    id: 'nav.suppliers',
    label: 'Proveedores',
    category: 'Navegacion',
    icon: Truck,
    path: '/suppliers',
    permission: 'procurement:read',
  },

  // Acciones nuevas
  {
    id: 'action.new-lab-order',
    label: 'Nueva orden de laboratorio',
    description: 'Crear orden para laboratorio dental',
    category: 'Acciones',
    icon: FlaskConical,
    path: '/lab/orders?new=1',
    keywords: ['laboratorio', 'orden', 'protesis', 'corona'],
    permission: ['lab:manage', 'lab:read'],
  },
  {
    id: 'action.new-expense',
    label: 'Nuevo gasto',
    description: 'Registrar gasto de clinica',
    category: 'Acciones',
    icon: Landmark,
    path: '/accounting/expenses?new=1',
    keywords: ['gasto', 'coste', 'egreso', 'pago'],
    permission: ['accounting:manage', 'accounting:read'],
  },
]

export function getDefaultEnabledActions(): string[] {
  return ALL_COMMAND_ACTIONS.map((a) => a.id)
}
